<template>
  <div class="mc-rec-dash">
    <b-breadcrumb class="mc-rec-dash__breadcrumb">
      <b-breadcrumb-item :to="{ name: 'campaign-ia' }">
        <feather-icon icon="ZapIcon" size="14" class="mr-50" />
        Campanhas Inteligentes
      </b-breadcrumb-item>
      <b-breadcrumb-item active>Resultados — WhatsApp</b-breadcrumb-item>
    </b-breadcrumb>

    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3" style="gap: 0.5rem;">
      <b-button variant="outline-secondary" size="sm" @click="voltar">
        <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
        Voltar
      </b-button>
      <b-button variant="outline-primary" size="sm" style="border-color: #5b028c; color: #5b028c;" @click="recarregarTudo" :disabled="carregando">
        <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
        Atualizar
      </b-button>
    </div>

    <b-card v-if="lojas.length > 1" class="border-0 mb-3" style="box-shadow: 0 4px 24px rgba(63,0,107,0.06); border-radius: 12px;">
      <label class="small font-weight-bold text-uppercase" style="color: #6e6781; letter-spacing: 0.04em;">Loja</label>
      <b-form-select v-model="lojaSelecionada" :options="lojasOptions" size="sm" @change="onLojaChange" />
    </b-card>

    <template v-if="lojaSelecionada">
      <RecommendationDashboardHeader
        :data-ativacao="dataAtivacao"
        :preset="preset"
        :custom-start="customStart"
        :custom-end="customEnd"
        :comparar-periodo-anterior="compararAnterior"
        @update:preset="preset = $event"
        @toggle-comparar="compararAnterior = $event"
        @aplicar-periodo="onAplicarPeriodo"
      />

      <RecommendationLifetimeSummary :loading="loadingLifetime" :dados="dadosLifetime" />

      <RecommendationPeriodSummary
        :loading="loadingPeriodo"
        :periodo-label="periodoSelecionadoLabel"
        :comparar="compararAnterior"
        :dados="dadosPeriodo"
        :dados-anterior="dadosPeriodoAnterior"
      />

      <RecommendationConversionFunnel :steps="passosFunil" />

      <b-row class="mc-rec-chart-products-row align-items-stretch">
        <b-col cols="12" lg="8" class="mb-4 mb-lg-0">
          <RecommendationPerformanceCharts
            :chart-receita="seriesReceita"
            :chart-conversao="seriesConversao"
            :chart-custo="seriesCustoSemanal"
          />
        </b-col>
        <b-col cols="12" lg="4">
          <RecommendationTopProducts
            :produtos="produtosEnriquecidos"
            :produtos-conversao-ranking="rankingConversaoPorPedidos"
            compact
          />
        </b-col>
      </b-row>

      <RecommendationInsights :blocos="blocosInsights" />

      <RecommendationPedidosHistorico
        :pedidos="pedidosConvertidosLista"
        :historico-envios="metricasPeriodo && metricasPeriodo.historico_envios"
      />
    </template>

    <b-card v-else class="border-0 text-center py-5" style="border-radius: 12px;">
      <p class="text-muted mb-0">Selecione uma loja para ver os resultados.</p>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BFormSelect,
  BBreadcrumb,
  BBreadcrumbItem,
  BRow,
  BCol,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import dayjs from 'dayjs'
import stores from '@/functions/basics/stores'
import {
  buscarMetricasRecomendacoes,
  listarRecomendacoes,
  verificarStatusRecomendacoes,
} from '@/functions/basics/recommendations'
import { normalizarMetricasPayload } from './utils/normalizarMetricasPayload'
import rankingProdutosPorComprasConvertidas from './utils/rankingProdutosComprados'
import { getDatesForPreset, previousPeriodRange } from './utils/periodPresets'
import RecommendationDashboardHeader from './RecommendationDashboardHeader.vue'
import RecommendationLifetimeSummary from './RecommendationLifetimeSummary.vue'
import RecommendationPeriodSummary from './RecommendationPeriodSummary.vue'
import RecommendationConversionFunnel from './RecommendationConversionFunnel.vue'
import RecommendationPerformanceCharts from './RecommendationPerformanceCharts.vue'
import RecommendationTopProducts from './RecommendationTopProducts.vue'
import RecommendationInsights from './RecommendationInsights.vue'
import RecommendationPedidosHistorico from './RecommendationPedidosHistorico.vue'
import './mc-recommendations-dashboard.scss'

const LIMIT = 100

export default {
  name: 'RecommendationResultsDashboard',
  components: {
    BCard,
    BButton,
    BFormSelect,
    BBreadcrumb,
    BBreadcrumbItem,
    BRow,
    BCol,
    FeatherIcon,
    RecommendationDashboardHeader,
    RecommendationLifetimeSummary,
    RecommendationPeriodSummary,
    RecommendationConversionFunnel,
    RecommendationPerformanceCharts,
    RecommendationTopProducts,
    RecommendationInsights,
    RecommendationPedidosHistorico,
  },
  data() {
    return {
      lojas: [],
      lojaSelecionada: null,
      preset: '30d',
      customStart: '',
      customEnd: '',
      compararAnterior: false,
      periodoStart: '',
      periodoEnd: '',
      metricasLifetime: null,
      metricasPeriodo: null,
      metricasAnterior: null,
      recomendacoesPeriodo: [],
      loadingLifetime: false,
      loadingPeriodo: false,
      carregando: false,
      dataAtivacao: '',
    }
  },
  computed: {
    lojasOptions() {
      return this.lojas.map(loja => ({
        value: loja.uuid || loja.id,
        text: loja.nome || loja.name || loja.store_name || 'Loja',
      }))
    },
    periodoSelecionadoLabel() {
      if (!this.periodoStart || !this.periodoEnd) return '—'
      return `${this.fmtDataBR(this.periodoStart)} até ${this.fmtDataBR(this.periodoEnd)}`
    },
    dadosLifetime() {
      return this.metricasToLifetime(this.metricasLifetime)
    },
    dadosPeriodo() {
      return this.metricasToPeriodo(this.metricasPeriodo)
    },
    dadosPeriodoAnterior() {
      return this.metricasToPeriodo(this.metricasAnterior)
    },
    passosFunil() {
      return this.buildFunnel(this.metricasPeriodo)
    },
    seriesReceita() {
      const h = this.weeklyFromHistorico(this.metricasPeriodo, 'receita')
      if (h.labels && h.labels.length) return h
      return this.weeklyProportionalFromTemplate(this.metricasPeriodo, 'receita')
    },
    seriesConversao() {
      const h = this.weeklyFromHistorico(this.metricasPeriodo, 'compras')
      if (h.labels && h.labels.length) return h
      return this.weeklyProportionalFromTemplate(this.metricasPeriodo, 'compras')
    },
    seriesCustoSemanal() {
      const r = this.seriesReceita
      const labels = r.labels || []
      const total = this.dadosPeriodo?.custo || 0
      const n = Math.max(1, labels.length)
      const porSemana = Math.round((total / n) * 100) / 100
      return { labels, data: labels.map(() => porSemana) }
    },
    pedidosConvertidosLista() {
      const p = this.metricasPeriodo && this.metricasPeriodo.pedidos_convertidos
      return Array.isArray(p) ? p : []
    },
    rankingConversaoPorPedidos() {
      return rankingProdutosPorComprasConvertidas(this.pedidosConvertidosLista, 5)
    },
    produtosEnriquecidos() {
      const raw = this.metricasPeriodo?.top_5_produtos_recomendados || []
      return raw.map(p => {
        let conversaoRaw = 0
        if (p.taxa_conversao != null) conversaoRaw = p.taxa_conversao
        else if (p.conversao != null) conversaoRaw = p.conversao
        return {
          ...p,
          product_name: p.product_name,
          vezes_recomendado: p.vezes_recomendado,
          conversao_raw: conversaoRaw,
          receita_gerada: p.receita_gerada != null ? p.receita_gerada : 0,
          pedidos_gerados: p.pedidos_gerados,
        }
      })
    },
    blocosInsights() {
      const d = this.dadosPeriodo
      const s = this.metricasPeriodo?.summary
      const hist = this.metricasPeriodo?.historico_conversoes
      const pedConv = this.pedidosConvertidosLista
      let tempoMedio = '—'
      let tempoSub = 'Entre registro e compra'
      if (Array.isArray(pedConv) && pedConv.length) {
        const secs = pedConv
          .map(x => x.segundos_ate_compra)
          .filter(x => x != null && !Number.isNaN(Number(x)))
        if (secs.length) {
          const avg = secs.reduce((a, b) => a + Number(b), 0) / secs.length
          const dias = avg / 86400
          tempoMedio = `${dias.toLocaleString('pt-BR', { maximumFractionDigits: 2, minimumFractionDigits: 0 })} dias (média após envio)`
          tempoSub = ''
        }
      }
      if (tempoMedio === '—' && Array.isArray(hist) && hist.length && hist[0].purchased_at) {
        const dias = hist
          .filter(h => h.purchased_at && h.created_at)
          .map(h => (new Date(h.purchased_at) - new Date(h.created_at)) / (86400000))
        if (dias.length) {
          const m = dias.reduce((a, b) => a + b, 0) / dias.length
          tempoMedio = `${m.toFixed(1)} dias (média)`
        }
      }
      return [
        { titulo: 'Clientes reativados (período)', valor: String(s?.para_quantos_clientes ?? d?.clientesImpactados ?? '—'), sub: 'Contato único no período' },
        { titulo: 'Tempo até conversão', valor: tempoMedio, sub: tempoSub },
        { titulo: 'Produtos distintos', valor: String(s?.produtos_recomendados_total ?? '—'), sub: 'Catálogo tocado' },
        { titulo: 'Mensagens enviadas', valor: String(s?.mensagens_enviadas ?? '—'), sub: 'Volume no período' },
      ]
    },
  },
  async created() {
    if (this.$route.query.loja) this.lojaSelecionada = this.$route.query.loja
    else {
      const salva = localStorage.getItem('lojaSelecionada')
      if (salva) this.lojaSelecionada = salva
    }
    await this.carregarLojas()
    const { start, end } = getDatesForPreset(this.preset, this.customStart, this.customEnd)
    this.periodoStart = start
    this.periodoEnd = end
    if (this.lojaSelecionada) await this.bootstrapLoja()
  },
  methods: {
    fmtDataBR(iso) {
      if (!iso) return '—'
      return dayjs(iso).format('DD/MM/YYYY')
    },
    async carregarLojas() {
      try {
        this.lojas = await stores() || []
        if (this.lojas.length > 0 && !this.lojaSelecionada) {
          this.lojaSelecionada = this.lojas[0].uuid || this.lojas[0].id
        }
      } catch (e) {
        console.error(e)
        this.lojas = []
      }
    },
    async bootstrapLoja() {
      await this.carregarStatusAtivacao()
      await Promise.all([this.carregarLifetime(), this.carregarPeriodoCompleto()])
    },
    async carregarStatusAtivacao() {
      try {
        const st = await verificarStatusRecomendacoes(this.lojaSelecionada)
        const c = st.campaign || st.configuracao
        const d = c?.created_at || c?.activated_at || st.updated_at
        this.dataAtivacao = d ? dayjs(d).format('DD/MM/YYYY') : ''
      } catch {
        this.dataAtivacao = ''
      }
    },
    async carregarLifetime() {
      this.loadingLifetime = true
      try {
        const res = await buscarMetricasRecomendacoes(this.lojaSelecionada, {})
        this.metricasLifetime = normalizarMetricasPayload(res, { escopo: 'geral' })
      } catch (e) {
        console.error(e)
        this.metricasLifetime = null
      } finally {
        this.loadingLifetime = false
      }
    },
    async onAplicarPeriodo(payload) {
      if (payload) {
        this.preset = payload.preset
        this.customStart = payload.customStart || ''
        this.customEnd = payload.customEnd || ''
      }
      const { start, end } = getDatesForPreset(this.preset, this.customStart, this.customEnd)
      this.periodoStart = start
      this.periodoEnd = end
      await this.carregarPeriodoCompleto()
    },
    async carregarPeriodoCompleto() {
      if (!this.lojaSelecionada) return
      this.loadingPeriodo = true
      this.carregando = true
      try {
        const params = { start_date: this.periodoStart, end_date: this.periodoEnd }
        const res = await buscarMetricasRecomendacoes(this.lojaSelecionada, params)
        this.metricasPeriodo = normalizarMetricasPayload(res, { escopo: 'periodo' })
        if (this.compararAnterior) {
          const { start, end } = previousPeriodRange(this.periodoStart, this.periodoEnd)
          const resA = await buscarMetricasRecomendacoes(this.lojaSelecionada, { start_date: start, end_date: end })
          this.metricasAnterior = normalizarMetricasPayload(resA, { escopo: 'periodo' })
        } else {
          this.metricasAnterior = null
        }
        const listRes = await listarRecomendacoes(this.lojaSelecionada, { ...params, limit: LIMIT, offset: 0 })
        const data = listRes.data || listRes
        this.recomendacoesPeriodo = data.recommendations || []
      } catch (e) {
        console.error(e)
        this.metricasPeriodo = null
        this.metricasAnterior = null
      } finally {
        this.loadingPeriodo = false
        this.carregando = false
      }
    },
    async onLojaChange() {
      localStorage.setItem('lojaSelecionada', this.lojaSelecionada)
      await this.bootstrapLoja()
    },
    async recarregarTudo() {
      await Promise.all([this.carregarLifetime(), this.carregarPeriodoCompleto()])
    },
    voltar() {
      this.$router.push({ name: 'campaign-ia' })
    },
    metricasToLifetime(n) {
      if (!n?.conversions) return null
      const c = n.conversions
      const s = n.summary || {}
      const receita = parseFloat(c.total_revenue) || 0
      const pedidos = Number(c.total_purchases) || 0
      const custo = (Number(s.mensagens_enviadas ?? s.recomendacoes_criadas ?? 0)) * 0.35
      const roiTexto = custo > 0 ? `${(receita / custo).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x` : '—'
      const conv = c.conversion_rate != null ? `${parseFloat(c.conversion_rate).toFixed(1)}%` : '—'
      const clientes = s.para_quantos_clientes ?? s.clientes_unicos_contactados ?? '—'
      return {
        receita,
        pedidos,
        roiTexto,
        conversao: conv,
        clientes,
      }
    },
    metricasToPeriodo(n) {
      if (!n?.conversions) return null
      const c = n.conversions
      const s = n.summary || {}
      const ta = n.templateAggregates
      const receita = parseFloat(c.total_revenue) || 0
      const pedidos = Number(c.total_purchases) || 0
      const custo = (Number(s.mensagens_enviadas ?? s.recomendacoes_criadas ?? 0)) * 0.35
      const roiNum = custo > 0 ? receita / custo : 0
      const roiTexto = custo > 0 ? `${roiNum.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}x` : '—'
      const convNum = parseFloat(c.conversion_rate) || 0
      const conversao = `${convNum.toFixed(1)}%`
      const hist = n.historico_conversoes
      let clicks = 0
      if (Array.isArray(hist) && hist.length && hist[0].purchased_at != null) {
        hist.forEach(ev => {
          if (ev.clicked) clicks += 1
        })
      }
      let ctrNum = null
      let clk = clicks
      if (ta != null) {
        clk = ta.clicked
      } else if (c.total_clicks != null) {
        clk = Number(c.total_clicks)
      }
      const lidos = ta != null && ta.read > 0 ? ta.read : null
      if (lidos != null && lidos > 0 && clk != null) {
        ctrNum = (Number(clk) / lidos) * 100
      } else if (c.click_through_rate != null) {
        const apiCtr = parseFloat(c.click_through_rate)
        if (!Number.isNaN(apiCtr)) ctrNum = apiCtr
      }
      const ctr = ctrNum != null && !Number.isNaN(ctrNum) ? `${ctrNum.toFixed(1)}%` : '—'
      const compras = pedidos || 1
      const cac = custo / compras
      return {
        receita,
        pedidos,
        roiTexto,
        roiNum,
        conversao,
        convNum,
        ctr,
        ctrNum: ctrNum != null && !Number.isNaN(ctrNum) ? ctrNum : 0,
        cac,
        custo,
        clientesImpactados: s.para_quantos_clientes,
        clientesConvertidos: pedidos,
        ticket: c.avg_order_value,
      }
    },
    buildFunnel(n) {
      if (!n?.summary) return []
      const s = n.summary
      const c = n.conversions || {}
      const m = n.metricasRaw || {}
      const ta = n.templateAggregates
      const hist = n.historico_conversoes || []
      /** Cliques: priorizar soma de template_analytics (Meta); total_clicks em conversions costuma vir 0 */
      let cliques = null
      if (ta != null) {
        cliques = ta.clicked
      } else if (m.total_cliques != null) {
        cliques = m.total_cliques
      } else if (c.total_clicks != null) {
        cliques = Number(c.total_clicks)
      } else if (Array.isArray(hist) && hist[0]?.purchased_at != null) {
        cliques = hist.filter(h => h.clicked).length
      }
      const geradas = Number(s.recomendacoes_criadas) || 0
      let enviadas = Number(s.mensagens_enviadas) || 0
      if (ta != null && ta.sent > 0) enviadas = ta.sent
      else if (!enviadas) enviadas = geradas
      let entregues = m.mensagens_entregues
      if (ta != null && ta.delivered > 0) entregues = ta.delivered
      else if (entregues == null) entregues = enviadas
      let lidas = m.mensagens_lidas ?? m.mensagens_lidas_whatsapp
      if (ta != null && ta.read > 0) lidas = ta.read
      const compras = Number(c.total_purchases) || 0
      const steps = [
        { key: 'g', label: 'Recomendações geradas', value: geradas },
        { key: 'e', label: 'Mensagens enviadas', value: enviadas },
        { key: 'd', label: 'Entregues', value: entregues },
        { key: 'l', label: 'Lidas', value: lidas != null ? lidas : '—' },
        { key: 'c', label: 'Cliques', value: cliques != null ? cliques : '—' },
        { key: 'p', label: 'Compras geradas', value: compras },
      ]
      let prev = null
      return steps.map(step => {
        let rateFromPrev = null
        if (prev != null && typeof step.value === 'number' && prev > 0 && typeof step.value === 'number') {
          rateFromPrev = Math.round((step.value / prev) * 1000) / 10
        }
        if (typeof step.value === 'number') prev = step.value
        return { ...step, rateFromPrev }
      })
    },
    weeklyFromHistorico(n, modo) {
      const hist = n?.historico_conversoes
      if (!Array.isArray(hist) || !hist.length) return { labels: [], data: [] }
      const primeiro = hist[0]
      if (!(primeiro.purchased_at != null || primeiro.purchased === true)) return { labels: [], data: [] }
      const porSemana = {}
      hist.forEach(ev => {
        const dataStr = ev.purchased_at ?? ev.created_at
        if (!dataStr) return
        const d = new Date(dataStr)
        if (Number.isNaN(d.getTime())) return
        const ano = d.getFullYear()
        const inicioAno = new Date(ano, 0, 1)
        const sem = Math.floor((d - inicioAno) / (7 * 24 * 60 * 60 * 1000)) + 1
        const key = `${ano}-S${String(sem).padStart(2, '0')}`
        if (!porSemana[key]) porSemana[key] = { count: 0, receita: 0 }
        porSemana[key].count += 1
        porSemana[key].receita += parseFloat(ev.order_value) || 0
      })
      const keys = Object.keys(porSemana).sort()
      const labels = keys.map((_, i) => `Sem ${i + 1}`)
      const data = keys.map(k => (modo === 'receita' ? porSemana[k].receita : porSemana[k].count))
      return { labels, data }
    },
    /** Agrupa pontos template_analytics (start em segundos) por “semana do ano” */
    weeklyFromTemplateDaily(n, field) {
      const pts = n?.templateDailyPoints || []
      if (!Array.isArray(pts) || !pts.length) return { labels: [], data: [] }
      const porSemana = {}
      for (let i = 0; i < pts.length; i += 1) {
        const pt = pts[i]
        const d = new Date(pt.start * 1000)
        if (!Number.isNaN(d.getTime())) {
          const ano = d.getFullYear()
          const inicioAno = new Date(ano, 0, 1)
          const sem = Math.floor((d - inicioAno) / (7 * 24 * 60 * 60 * 1000)) + 1
          const key = `${ano}-S${String(sem).padStart(2, '0')}`
          if (!porSemana[key]) porSemana[key] = 0
          porSemana[key] += pt[field] || 0
        }
      }
      const keys = Object.keys(porSemana).sort()
      const labels = keys.map((_, idx) => `Sem ${idx + 1}`)
      const data = keys.map(k => porSemana[k])
      return { labels, data }
    },
    /**
     * Quando não há historico_conversoes: distribui receita/pedidos do período
     * proporcionalmente ao volume de envios por semana (template_analytics).
     */
    weeklyProportionalFromTemplate(n, tipo) {
      const base = this.weeklyFromTemplateDaily(n, 'sent')
      if (!base.labels.length) return { labels: [], data: [] }
      const c = n?.conversions || {}
      const sumW = base.data.reduce((a, b) => a + b, 0) || 1
      if (tipo === 'receita') {
        const total = parseFloat(c.total_revenue) || 0
        return {
          labels: base.labels,
          data: base.data.map(s => Math.round(((s / sumW) * total) * 100) / 100),
        }
      }
      const total = Number(c.total_purchases) || 0
      return {
        labels: base.labels,
        data: base.data.map(s => Math.round((s / sumW) * total)),
      }
    },
  },
  watch: {
    compararAnterior() {
      this.carregarPeriodoCompleto()
    },
  },
}
</script>
